import abc from '../../layouts/abc.json';
import abc10col from '../../layouts/abc-10col.json';
import deCH from '../../layouts/de-CH.json';
import deCH10col from '../../layouts/de-CH-10col.json';
import digits10col from '../../layouts/digits-10col.json';
import digits11col from '../../layouts/digits-11col.json';
import type { LayoutId } from '../types';

/**
 * Keyboard layouts are **data, not code** (§4.3), so all of them ship and all of them can be
 * tested, and switching is a setting rather than a release.
 *
 * ── Two kinds of board, two registries (ADR-039) ──
 *
 * A **letter board** is what `keyboardLayout` selects and what the person has learned. A **layer** is the
 * second board the `123`/`ABC` switch reaches, and it answers every question a letter board does except one:
 * it has no letters. That single difference is why they are separate registries instead of one list with an
 * exception in it — `availableLayouts()` feeds a caregiver's picker and the "all 29 German letters" assertion
 * runs over everything it returns, so a layer in that list would either appear as something to choose or
 * force the assertion to grow a special case. Both are worse than a second `Record`.
 *
 * A layer is **not** a `LayoutId`. It never enters `LAYOUT_COLUMNS` or `WIDE_GAP_LAYOUT`, which are exhaustive
 * over that union and already carry one entry describing a board that does not exist (C77). ADR-036 counted
 * every new id touching both tables as a cost of the second layer; keeping layers out of the union is how that
 * cost is not paid.
 */

export interface LayoutKey {
  /** What gets inserted into the composer. A single space for the space key. */
  key: string;
  /** What is drawn. Defaults to `key`. */
  label?: string;
  /** Accessible name where the glyph is not a word — `␣` reads as "Leerzeichen". */
  name?: string;
  /**
   * Columns this key covers. Defaults to 1, and only the space key uses anything else.
   *
   * Keys are square so muscle memory has one shape to learn, and that rule is still the default — this
   * is the single exception the design earns. Space is pressed once per word, more than any letter, and
   * it was a 19 mm square in the corner of the row with `,` and `.` for neighbours: the most-used key on
   * the board, sized like the least-used and flanked by the two things it is worst to hit instead.
   *
   * A row's spans must sum to `columns`. Nothing checks it at runtime — `keyboardMetrics` divides the
   * width by `columns` and a row that oversubscribes simply overflows — so `tests/unit/layouts.test.ts`
   * asserts it for every shipped layout, where the failure is a red test rather than a bent keyboard.
   */
  span?: number;
}

export interface KeyboardLayout {
  id: string;
  name: string;
  columns: number;
  note: string;
  rows: LayoutKey[][];
}

const LAYOUTS: Partial<Record<LayoutId, KeyboardLayout>> = {
  'de-CH': deCH as KeyboardLayout,
  'de-CH-10col': deCH10col as KeyboardLayout,
  abc: abc as KeyboardLayout,
  'abc-10col': abc10col as KeyboardLayout,
  // `frequency` is named in the Settings contract and is not built: a layout ordered by letter
  // frequency minimises scan steps, but it also has no order the person can predict, which is the
  // one thing `abc` is for. It needs the diagnostics 3.4 is waiting on before it is worth the
  // motor memory. Falling back rather than throwing means a stale setting can never leave the
  // person without a keyboard.
};

export function getLayout(id: LayoutId): KeyboardLayout {
  const layout = LAYOUTS[id];
  if (layout) return layout;
  console.warn(`[layouts] ${id} not available, falling back to de-CH`);
  return LAYOUTS['de-CH'] as KeyboardLayout;
}

export function availableLayouts(): KeyboardLayout[] {
  return Object.values(LAYOUTS).filter((l): l is KeyboardLayout => l !== undefined);
}

/** Which second board the `123`/`ABC` switch reaches. Not a `LayoutId` — see the note at the top. */
export type LayerId = 'digits-11col' | 'digits-10col';

const LAYERS: Record<LayerId, KeyboardLayout> = {
  'digits-11col': digits11col as KeyboardLayout,
  'digits-10col': digits10col as KeyboardLayout,
};

/**
 * The digit layer paired with each letter board.
 *
 * **Paired by table, not by arithmetic on the column count**, for the reason `WIDE_GAP_LAYOUT` is a table:
 * ADR-036 wanted the layer paired with its board so `abc` and `de-CH` can diverge if there is ever a reason,
 * and a `columns === 10 ? … : …` expression forecloses that while looking like it saves something. Two layers
 * cover four boards today only because nothing yet distinguishes them beyond width.
 *
 * `frequency` is the entry to read twice. It has no JSON, so `getLayout` hands back eleven-column `de-CH`, and
 * the layer here has to match **what renders** rather than what `LAYOUT_COLUMNS` claims — that table says
 * `frequency: 10` and is wrong (C77). Getting it right here rather than copying the neighbouring table is the
 * whole of C77's lesson.
 */
export const LAYER_FOR: Record<LayoutId, LayerId> = {
  'de-CH': 'digits-11col',
  'de-CH-10col': 'digits-10col',
  abc: 'digits-11col',
  'abc-10col': 'digits-10col',
  frequency: 'digits-11col',
};

/** The digit layer for a letter board. Total by type, so a new layout cannot ship without a layer. */
export function getLayer(id: LayoutId): KeyboardLayout {
  return LAYERS[LAYER_FOR[id]];
}

/**
 * Every layer, for the assertions that must sweep them.
 *
 * Deliberately separate from `availableLayouts()` rather than folded into it: the tests that run over that
 * list assert things only a letter board owes, and a sweep that silently grew to include layers would either
 * fail or be weakened. Two functions, two sets of rules, and `layouts.test.ts` says which is which.
 */
export function layerLayouts(): KeyboardLayout[] {
  return Object.values(LAYERS);
}

/**
 * The wide-gap partner of a layout: the same letters in the same order, over ten columns instead of
 * eleven. Widening the gaps must never move the letters.
 *
 * Low-effort mode and calibration both reach for ten columns when the problem is wrong-key hits
 * rather than misses — 3.4 mm between keys instead of 1.5 mm, at a cost of 0.35 mm of key size.
 * Before this map they reached for `de-CH-10col` by name, so a person on `abc` who tapped
 * `Leichter` got QWERTZ: every letter somewhere else, at the moment they had least to spend on
 * finding it. Exhaustive by type, so a future layout cannot ship without someone deciding what
 * widening it means.
 *
 * **Idempotent, and that is load-bearing rather than incidental.** `exitLowEffort` recomputes the
 * imposed values from the *current* settings to work out which fields are still its own to give
 * back, so this applied to its own output must not move again.
 */
export const WIDE_GAP_LAYOUT: Record<LayoutId, LayoutId> = {
  'de-CH': 'de-CH-10col',
  'de-CH-10col': 'de-CH-10col',
  abc: 'abc-10col',
  'abc-10col': 'abc-10col',
  // Unbuilt, so it renders as `de-CH` anyway; widening it lands where that widens.
  frequency: 'de-CH-10col',
};

export const wideGapLayout = (id: LayoutId): LayoutId => WIDE_GAP_LAYOUT[id];

export const keyLabel = (key: LayoutKey): string => key.label ?? key.key;

export const keyName = (key: LayoutKey): string => key.name ?? key.label ?? key.key;

export const keySpan = (key: LayoutKey): number => key.span ?? 1;

/** Columns a row occupies. Should equal `layout.columns` for every row of every layout. */
export const rowSpan = (row: readonly LayoutKey[]): number =>
  row.reduce((total, key) => total + keySpan(key), 0);

/** Row count, needed by the height half of the key-size calculation. */
export const rowCount = (layout: KeyboardLayout): number => layout.rows.length;
